import React, { useState } from 'react';
import { 
  Calculator, MapPin, Truck, Mountain, Search,
  ChevronDown, Info, Package, ArrowRight
} from 'lucide-react';
import { carrieres, zones, produitsTarifs, formatMontant } from '../data/mockData';

const TarifCalcul = () => {
  const [carriereId, setCarriereId] = useState('');
  const [adresse, setAdresse] = useState('');
  const [codePostal, setCodePostal] = useState('');
  const [ville, setVille] = useState('');
  const [selectedZone, setSelectedZone] = useState(null);
  const [categorieFilter, setCategorieFilter] = useState('toutes');

  const handleCalculer = () => {
    if (!carriereId || !adresse || !ville) return;
    const hash = (adresse.length + ville.length + codePostal.length) % 10;
    setSelectedZone(Math.max(1, Math.min(10, hash + 1)));
  };

  const carriere = carrieres.find(c => c.id === carriereId);

  const produitsDisponibles = carriereId
    ? produitsTarifs.filter(p => p.carrieres.includes(carriereId))
    : [];

  const categories = ['toutes', ...new Set(produitsDisponibles.map(p => p.categorie))];

  const produitsFiltres = categorieFilter === 'toutes'
    ? produitsDisponibles
    : produitsDisponibles.filter(p => p.categorie === categorieFilter);

  const groupedByCategorie = produitsFiltres.reduce((acc, p) => {
    if (!acc[p.categorie]) acc[p.categorie] = [];
    acc[p.categorie].push(p);
    return acc;
  }, {});

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1 className="page-title">Calcul de tarifs</h1>
          <p className="page-subtitle">Estimez le coût de vos matériaux selon la carrière de départ et la destination</p>
        </div>
      </div>

      {/* Formulaire de recherche */}
      <div className="tarif-form-card">
        <div className="tarif-form-grid">
          {/* Carrière de départ */}
          <div className="tarif-form-section">
            <div className="tarif-form-section-header">
              <div className="tarif-section-icon tarif-icon-amber">
                <Mountain size={20} />
              </div>
              <div>
                <h3>Carrière de départ</h3>
                <p>Sélectionnez le site d'extraction</p>
              </div>
            </div>
            <div className="tarif-select-wrapper">
              <select
                value={carriereId}
                onChange={(e) => {
                  setCarriereId(e.target.value);
                  setSelectedZone(null);
                  setCategorieFilter('toutes');
                }}
              >
                <option value="">-- Choisir une carrière --</option>
                {carrieres.map(c => (
                  <option key={c.id} value={c.id}>{c.nom}</option>
                ))}
              </select>
            </div>
            {carriere && (
              <div className="tarif-carriere-info">
                <MapPin size={14} />
                <span>{carriere.adresse}</span>
              </div>
            )}
          </div>

          {/* Destination */}
          <div className="tarif-form-section">
            <div className="tarif-form-section-header">
              <div className="tarif-section-icon tarif-icon-blue">
                <Truck size={20} />
              </div>
              <div>
                <h3>Adresse du chantier</h3>
                <p>Indiquez la destination de livraison</p>
              </div>
            </div>
            <div className="tarif-address-fields">
              <input
                type="text"
                placeholder="Adresse (n° et rue)"
                value={adresse}
                onChange={(e) => { setAdresse(e.target.value); setSelectedZone(null); }}
              />
              <div className="tarif-address-row">
                <input
                  type="text"
                  placeholder="Code postal"
                  value={codePostal}
                  onChange={(e) => { setCodePostal(e.target.value); setSelectedZone(null); }}
                  className="tarif-cp"
                />
                <input
                  type="text"
                  placeholder="Ville"
                  value={ville}
                  onChange={(e) => { setVille(e.target.value); setSelectedZone(null); }}
                />
              </div>
            </div>
          </div>
        </div>

        <button
          className="tarif-submit"
          onClick={handleCalculer}
          disabled={!carriereId || !adresse || !ville}
        >
          <Calculator size={18} />
          Calculer les tarifs
        </button>
      </div>

      {/* Résultat : zone détectée */}
      {selectedZone && carriere && (
        <>
          <div className="tarif-zone-banner">
            <div className="tarif-zone-banner-left">
              <div className="tarif-zone-route">
                <span className="tarif-zone-point">
                  <Mountain size={16} />
                  {carriere.nom}
                </span>
                <ArrowRight size={18} className="tarif-zone-arrow" />
                <span className="tarif-zone-point">
                  <MapPin size={16} />
                  {ville} ({codePostal})
                </span>
              </div>
              <div className="tarif-zone-detail">
                <Info size={14} />
                <span>Distance estimée : <strong>{zones[selectedZone - 1].distance}</strong></span>
              </div>
            </div>
            <div className="tarif-zone-badge">
              Zone {selectedZone}
            </div>
          </div>

          {/* Filtre par catégorie */}
          <div className="tarif-categories">
            {categories.map(cat => (
              <button
                key={cat}
                className={`tarif-cat-btn ${categorieFilter === cat ? 'active' : ''}`}
                onClick={() => setCategorieFilter(cat)}
              >
                {cat === 'toutes' ? 'Tous les produits' : cat}
              </button>
            ))}
          </div>

          {/* Tableau des produits */}
          <div className="tarif-results-card">
            {Object.entries(groupedByCategorie).map(([categorie, produits]) => (
              <div key={categorie} className="tarif-categorie-group">
                <div className="tarif-categorie-header">
                  <Package size={16} />
                  <span>{categorie}</span>
                  <span className="tarif-categorie-count">{produits.length} produit{produits.length > 1 ? 's' : ''}</span>
                </div>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Produit</th>
                      <th>Unité</th>
                      <th style={{ textAlign: 'right' }}>Prix départ carrière</th>
                      <th style={{ textAlign: 'right' }}>Prix rendu chantier (Zone {selectedZone})</th>
                    </tr>
                  </thead>
                  <tbody>
                    {produits.map(produit => {
                      const prixZone = produit.prixParZone[carriereId][selectedZone - 1];
                      const prixDepart = produit.prixParZone[carriereId][0];
                      const transport = prixZone - prixDepart;
                      return (
                        <tr key={produit.code}>
                          <td><span className="mono-text">{produit.code}</span></td>
                          <td style={{ fontWeight: 500 }}>{produit.nom}</td>
                          <td>{produit.unite}</td>
                          <td style={{ textAlign: 'right' }}>{formatMontant(prixDepart)}</td>
                          <td style={{ textAlign: 'right' }}>
                            <div className="tarif-prix-cell">
                              <span className="tarif-prix-total">{formatMontant(prixZone)}</span>
                              {selectedZone > 1 && (
                                <span className="tarif-prix-transport">dont {formatMontant(transport)} transport</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ))}
          </div>

          {/* Grille complète des zones */}
          <div className="tarif-zones-card">
            <h2 className="tarif-zones-title">
              <Info size={18} />
              Grille tarifaire complète — Départ {carriere.nom}
            </h2>
            <div className="tarif-zones-table-wrapper">
              <table className="data-table tarif-zones-table">
                <thead>
                  <tr>
                    <th>Produit</th>
                    {zones.map(z => (
                      <th key={z.id} className={z.id === selectedZone ? 'tarif-zone-active-col' : ''} style={{ textAlign: 'right' }}>
                        Z{z.id}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {produitsDisponibles.map(produit => (
                    <tr key={produit.code}>
                      <td style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>{produit.nom}</td>
                      {zones.map(z => {
                        const prix = produit.prixParZone[carriereId][z.id - 1];
                        return (
                          <td
                            key={z.id}
                            className={z.id === selectedZone ? 'tarif-zone-active-col' : ''}
                            style={{ textAlign: 'right', fontSize: '0.8125rem' }}
                          >
                            {prix.toFixed(2)} €
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="tarif-zones-legend">
              {zones.map(z => (
                <span key={z.id} className={z.id === selectedZone ? 'tarif-legend-active' : ''}>
                  <strong>Z{z.id}</strong> {z.distance}
                </span>
              ))}
            </div>
          </div>
        </>
      )}

      {/* État vide */}
      {!selectedZone && carriereId && (
        <div className="empty-state" style={{ marginTop: '2rem' }}>
          <MapPin size={48} strokeWidth={1} />
          <h3>Renseignez l'adresse du chantier</h3>
          <p>Saisissez l'adresse de destination puis cliquez sur « Calculer les tarifs »</p>
        </div>
      )}

      {!carriereId && (
        <div className="empty-state" style={{ marginTop: '2rem' }}>
          <Mountain size={48} strokeWidth={1} />
          <h3>Sélectionnez une carrière de départ</h3>
          <p>Choisissez parmi SETEG, LA MÈDE ou SALON pour consulter les tarifs</p>
        </div>
      )}
    </div>
  );
};

export default TarifCalcul;
